/**
 * Board Finding Panel — slide-over detail panel for a single board report finding.
 *
 * Displays finding summary, date, document type, flags with details,
 * confidence, parties, source documents, and change history.
 * Follows the same Sheet pattern as AssetPanel and LiabilityPanel.
 */

import { useState } from "react";
import ReactMarkdown from "react-markdown";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Separator } from "@/ui/primitives/separator";
import { FileText, ExternalLink, Calendar, AlertCircle } from "lucide-react";
import { cn } from "@/utils/misc";
import { ChangeHistory } from "../shared-panels/ChangeHistory";
import { DocumentDetail } from "../../documents/DocumentDetail";
import type { BoardReportFindingUI } from "./types";
import {
  FlagDismissButton,
  FlagDismissEditor,
  flagGlowClass,
  type UseFlagDismissalsResult,
} from "../flags";

// ---------------------------------------------------------------------------
// Collapsible markdown notes
// ---------------------------------------------------------------------------

function normaliseNotesForMarkdown(raw: string): string {
  return raw.replace(/\s*•\s*/g, "\n- ").trim();
}

function CollapsibleNotes({ notes }: { notes: string }) {
  const [expanded, setExpanded] = useState(false);
  const normalisedNotes = notes ? normaliseNotesForMarkdown(notes) : "";

  if (!normalisedNotes)
    return <p className="text-sm text-muted-foreground">—</p>;

  return (
    <div>
      <div
        className={cn(
          "prose prose-sm relative max-w-none overflow-hidden transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
          !expanded && "max-h-[180px]",
        )}
      >
        <ReactMarkdown>{normalisedNotes}</ReactMarkdown>
        {!expanded && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const FLAG_CONFIG: Record<
  string,
  {
    label: string;
    color: string;
    bg: string;
    darkBg: string;
    darkColor: string;
  }
> = {
  financial_distress: {
    label: "Financial distress",
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    darkBg: "dark:bg-red-950 dark:border-red-800",
    darkColor: "dark:text-red-300",
  },
  turnaround: {
    label: "Turnaround",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    darkBg: "dark:bg-amber-950 dark:border-amber-800",
    darkColor: "dark:text-amber-300",
  },
  safe_harbour: {
    label: "Safe harbour",
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
    darkBg: "dark:bg-blue-950 dark:border-blue-800",
    darkColor: "dark:text-blue-300",
  },
  material_transaction: {
    label: "Material transaction",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    darkBg: "dark:bg-emerald-950 dark:border-emerald-800",
    darkColor: "dark:text-emerald-300",
  },
};

const DOC_TYPE_LABELS: Record<string, string> = {
  board_minutes: "Board Minutes",
  board_report: "Board Report",
  circular_resolution: "Circular Resolution",
  pre_appointment_minutes: "Pre-Appointment Minutes",
};

const CONFIDENCE_STYLES: Record<string, { badge: BadgeColor; color: string }> =
  {
    high: { badge: "emerald", color: "text-emerald-700 dark:text-emerald-400" },
    medium: { badge: "amber", color: "text-amber-700 dark:text-amber-400" },
    low: { badge: "red", color: "text-red-700 dark:text-red-400" },
  };

function fmtDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BoardFindingPanelProps {
  entry: BoardReportFindingUI | null;
  open: boolean;
  onClose: () => void;
  flagCtx: UseFlagDismissalsResult;
}

export function BoardFindingPanel({
  entry,
  open,
  onClose,
  flagCtx,
}: BoardFindingPanelProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  return (
    <>
      <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent className="flex w-full flex-col overflow-y-auto p-0 sm:max-w-xl">
          {entry && (
            <BoardFindingPanelContent
              entry={entry}
              flagCtx={flagCtx}
              onViewDocument={(docId) =>
                setViewingDocumentId(docId as Id<"documents">)
              }
            />
          )}
        </SheetContent>
      </Sheet>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

function BoardFindingPanelContent({
  entry,
  flagCtx,
  onViewDocument,
}: {
  entry: BoardReportFindingUI;
  flagCtx: UseFlagDismissalsResult;
  onViewDocument: (docId: string) => void;
}) {
  const confStyle =
    CONFIDENCE_STYLES[entry.confidence] ?? CONFIDENCE_STYLES.low;
  const flagDetail = entry.flagDetails ?? "";

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-6 pb-4 pt-6">
        <SheetHeader className="space-y-1">
          <SheetTitle className="flex items-center gap-2 text-base font-semibold leading-tight">
            {DOC_TYPE_LABELS[entry.documentType] ?? entry.documentType}
            <span className="font-normal text-muted-foreground">—</span>
            <span className="flex items-center gap-1 font-normal tabular-nums text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {fmtDate(entry.date)}
            </span>
          </SheetTitle>
        </SheetHeader>
      </div>

      {/* Flags Banner */}
      {entry.flags.length > 0 && (
        <div className="border-y bg-muted/30 px-6 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Flags
          </p>
          <div className="flex flex-wrap gap-1.5">
            {entry.flags.map((flag) => {
              const cfg = FLAG_CONFIG[flag];
              if (!cfg) return null;
              const dismissed = flagCtx.isDismissed("boardReports", entry._id, {
                type: flag,
                detail: flagDetail,
              });
              return (
                <span key={flag} className="inline-flex items-center gap-0.5">
                  <span
                    className={cn(
                      "inline-flex items-center whitespace-nowrap rounded border px-2 py-1 text-xs font-medium",
                      cfg.bg,
                      cfg.color,
                      cfg.darkBg,
                      cfg.darkColor,
                      dismissed
                        ? "line-through opacity-40"
                        : flagGlowClass(true),
                    )}
                  >
                    {cfg.label}
                  </span>
                  <FlagDismissButton
                    ctx={flagCtx}
                    agentKind="boardReports"
                    entryId={entry._id}
                    flagType={flag}
                    flagDetail={flagDetail}
                  />
                </span>
              );
            })}
          </div>
          {entry.flags.map((flag) => {
            const cfg = FLAG_CONFIG[flag];
            if (!cfg) return null;
            return (
              <FlagDismissEditor
                key={flag}
                ctx={flagCtx}
                agentKind="boardReports"
                entryId={entry._id}
                entryLabel={entry.summary}
                flagType={flag}
                flagLabel={cfg.label}
                flagDetail={flagDetail}
              />
            );
          })}
        </div>
      )}

      <Separator />

      {/* Scrollable body */}
      <ScrollArea className="min-h-0 min-w-0 flex-1">
        <div className="min-w-0 space-y-6 px-6 py-5">
          {/* Summary */}
          {entry.summary && (
            <PanelSection title="Summary">
              <p className="text-sm leading-relaxed text-foreground">
                {entry.summary}
              </p>
            </PanelSection>
          )}

          {/* Flag Details */}
          {entry.flagDetails && (
            <PanelSection title="Flag Details">
              <CollapsibleNotes notes={entry.flagDetails} />
            </PanelSection>
          )}

          {/* Confidence */}
          <PanelSection title="Confidence">
            <Badge color={confStyle.badge} variant="soft" size="sm">
              {entry.confidence.charAt(0).toUpperCase() +
                entry.confidence.slice(1)}
            </Badge>
          </PanelSection>

          {/* Parties */}
          {entry.parties.length > 0 && (
            <PanelSection title="Parties" count={entry.parties.length}>
              <div className="flex flex-wrap gap-1.5">
                {entry.parties.map((party, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center whitespace-nowrap rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
                  >
                    {party}
                  </span>
                ))}
              </div>
            </PanelSection>
          )}

          {/* Manual Override indicator */}
          {entry.isManualOverride && (
            <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/20">
              <AlertCircle className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" />
              <span className="text-xs font-medium text-violet-700 dark:text-violet-400">
                Manually overridden by practitioner
              </span>
            </div>
          )}

          {/* Source Documents */}
          {entry.sourceDocuments.length > 0 && (
            <PanelSection
              title="Source Documents"
              count={entry.sourceDocuments.length}
            >
              <div className="space-y-1.5">
                {entry.sourceDocuments.map((doc, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      if (doc.documentId) onViewDocument(doc.documentId);
                    }}
                    disabled={!doc.documentId}
                    className={cn(
                      "group flex w-full min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 text-left",
                      doc.documentId
                        ? "cursor-pointer border-border bg-background transition-colors hover:bg-muted/50"
                        : "cursor-default border-border bg-muted/30",
                    )}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 break-words text-sm text-foreground">
                      {doc.fileName}
                    </span>
                    {doc.documentId && (
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </button>
                ))}
              </div>
            </PanelSection>
          )}

          {/* History (collapsible) */}
          <ChangeHistory history={entry.changeHistory} />
        </div>
      </ScrollArea>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel Section
// ---------------------------------------------------------------------------

function PanelSection({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
          {title}
        </h4>
        {count != null && (
          <Badge variant="secondary" size="sm">
            {count}
          </Badge>
        )}
      </div>
      {children}
    </div>
  );
}
